import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { CheckCircle2, XCircle, AlertCircle, Info, X } from 'lucide-react'
import { cn } from '@/lib/cn'

export type ToastKind = 'success' | 'error' | 'warning' | 'info'

interface Toast {
  id: string
  kind: ToastKind
  title: string
  description?: string
}

interface ToastContextValue {
  push: (kind: ToastKind, title: string, description?: string) => void
  success: (title: string, description?: string) => void
  error: (title: string, description?: string) => void
}

const ToastContext = createContext<ToastContextValue | null>(null)

const ICON_BY_KIND: Record<ToastKind, typeof CheckCircle2> = {
  success: CheckCircle2,
  error: XCircle,
  warning: AlertCircle,
  info: Info,
}

const COLOR_BY_KIND: Record<ToastKind, string> = {
  success: 'text-jarvis-green border-jarvis-green/30 bg-jarvis-green/5',
  error: 'text-jarvis-red border-jarvis-red/30 bg-jarvis-red/5',
  warning: 'text-jarvis-amber border-jarvis-amber/30 bg-jarvis-amber/5',
  info: 'text-jarvis-blue border-jarvis-blue/30 bg-jarvis-blue/5',
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([])
  const idRef = useRef(0)

  const dismiss = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id))
  }, [])

  const push = useCallback<ToastContextValue['push']>(
    (kind, title, description) => {
      const id = `t-${idRef.current++}`
      setToasts((prev) => [...prev, { id, kind, title, description }])
      window.setTimeout(() => dismiss(id), 5000)
    },
    [dismiss],
  )

  // Memoize so the context value keeps a stable identity across renders.
  // Otherwise every consumer effect that depends on the toast API (e.g. the
  // web-call error toast in CommandCenter) re-fires on each render — which is
  // what spawned a dozen duplicate toasts from a single failed call.
  const value = useMemo<ToastContextValue>(
    () => ({
      push,
      success: (title, description) => push('success', title, description),
      error: (title, description) => push('error', title, description),
    }),
    [push],
  )

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2 pointer-events-none">
        {toasts.map((t) => {
          const Icon = ICON_BY_KIND[t.kind]
          return (
            <div
              key={t.id}
              className={cn(
                'pointer-events-auto min-w-[320px] max-w-sm border rounded-lg p-3 pr-2',
                'bg-jarvis-surface backdrop-blur-sm shadow-lg flex items-start gap-3',
                COLOR_BY_KIND[t.kind],
              )}
              role="status"
            >
              <Icon size={18} className="flex-shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-jarvis-text">{t.title}</div>
                {t.description && (
                  <div className="text-xs text-jarvis-muted mt-0.5">
                    {t.description}
                  </div>
                )}
              </div>
              <button
                onClick={() => dismiss(t.id)}
                className="text-jarvis-muted hover:text-jarvis-text p-1"
                aria-label="Dismiss"
              >
                <X size={14} />
              </button>
            </div>
          )
        })}
      </div>
    </ToastContext.Provider>
  )
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    // Graceful fallback during initial render — log to console
    return {
      push: (_k, t) => console.log(`[toast] ${t}`),
      success: (t) => console.log(`[toast:success] ${t}`),
      error: (t) => console.error(`[toast:error] ${t}`),
    }
  }
  return ctx
}
